import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CreationOptions,
  CatalogResponse,
  PalletChoice,
  ProductCreateRequest,
  ProductCreateResponse,
  PackageLabelResponse
} from '../products-preview/product-creation.types';

export interface ProductRow {
  row_id: string;
  record_kind: 'product' | 'package';
  record_uuid: string;
  product_uuid: string | null;
  package_uuid: string | null;
  customer_key: string | null;
  customer_name: string;
  customer_source: string;
  package_customer_name: string | null;
  customer_conflict: boolean;
  description: string | null;
  model: string | null;
  product_code: string | null;
  serial: string | null;
  barcode: string | null;
  package_id: string | null;
  warehouse_uuid: string | null;
  warehouse_name: string | null;
  position_uuid: string | null;
  spot: string | null;
  position_since: string | null;
  available: boolean | null;
  delivered: boolean | null;
  created: string | null;
  intake_date: string | null;
  modified: string | null;
  declared_quantity: number | null;
  po_number: string | null;
  so_number: string | null;
  asn_number: string | null;
  bol_number: string | null;
  trip_number: string | null;
  trip_uuid: string | null;
  shipment_uuid: string | null;
  lading_uuid: string | null;
  weight: string | null;
  length: string | null;
  width: string | null;
  height: string | null;
  condition: string | null;
  return_status: string | null;
  carton_resolution: string | null;
  row_count?: number;
}

export interface ProductResponse {
  ok: boolean;
  data: ProductRow[];
  row_count: number;
  record_count: number;
  grouped: boolean;
}

export interface ProductOptions {
  ok: boolean;
  customers: { customer_key: string; customer_name: string; record_count: number }[];
  warehouses: { warehouse_uuid: string; warehouse_name: string }[];
  export_limit: number;
}

export interface ProductDetail {
  ok: boolean;
  record: ProductRow;
  contents: {
    product_uuid: string;
    model: string | null;
    serial: string | null;
    barcode: string | null;
    description: string | null;
  }[];
  contents_more: boolean;
  work_orders: { lading_uuid: string; reference: string | null; status: string | null }[];
  work_orders_more: boolean;
  history: {
    event_type: string;
    recorded_at: string;
    reason: string | null;
    source: string | null;
  }[];
  history_more: boolean;
}

export type ProductParams = Record<string, string>;

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private readonly url = '/api/products/products.php';

  constructor(private readonly http: HttpClient) {}

  list(query: ProductParams): Observable<ProductResponse> {
    return this.http.get<ProductResponse>(this.url, { params: this.params('list', query) });
  }

  options(): Observable<ProductOptions> {
    return this.http.get<ProductOptions>(this.url, { params: this.params('filters', {}) });
  }

  detail(row: ProductRow): Observable<ProductDetail> {
    return this.http.get<ProductDetail>(this.url, {
      params: this.params('detail', { kind: row.record_kind, uuid: row.record_uuid })
    });
  }

  export(query: ProductParams): Observable<Blob> {
    return this.http.get(this.url, { params: this.params('export', query), responseType: 'blob' });
  }

  creationOptions(customer?: string): Observable<CreationOptions> {
    return this.http.get<CreationOptions>(this.url, {
      params: this.params('creation-options', customer ? { customer_contact_uuid: customer } : {})
    });
  }

  catalog(customer: string, q: string): Observable<CatalogResponse> {
    return this.http.get<CatalogResponse>(this.url, {
      params: this.params('catalog', { customer_contact_uuid: customer, q })
    });
  }

  pallets(customer: string, q: string): Observable<{ ok: boolean; pallets: PalletChoice[] }> {
    return this.http.get<{ ok: boolean; pallets: PalletChoice[] }>(this.url, {
      params: this.params('pallets', { customer_contact_uuid: customer, q })
    });
  }

  create(body: ProductCreateRequest, key: string): Observable<ProductCreateResponse> {
    return this.http.post<ProductCreateResponse>(this.url, body, {
      params: this.params('create', {}),
      headers: { 'Idempotency-Key': key }
    });
  }

  packageLabel(uuid: string): Observable<PackageLabelResponse> {
    return this.http.get<PackageLabelResponse>(this.url, {
      params: this.params('label', { uuid })
    });
  }

  private params(action: string, values: ProductParams): HttpParams {
    let params = new HttpParams().set('action', action);
    for (const [key, value] of Object.entries(values)) {
      if (value !== '') {
        params = params.set(key, value);
      }
    }
    return params;
  }
}
